"use client";

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useDebounce } from "use-debounce";

export interface NavOption {
  id: string;
  label: string;
  value: string;
}

interface ResourceNavContextValue {
  currentSection: string;
  onSelect: (sectionId: string) => void;
  options: ResourceNavOptions;
  setOptions: (options: ResourceNavOptions) => void;
}

const ResourceNavContext = createContext<ResourceNavContextValue | null>(null);

interface ResourceNavOptions {
  offsetElement?: HTMLElement;
  additionalOffset?: number;
}

/**
 * Bottom edge of the offset element in viewport coordinates. Sticky elements
 * resolve to their pinned position rather than their current one, since that
 * is where they will sit once the scroll settles.
 */
const getOffsetBottom = (element?: HTMLElement) => {
  const target = element ?? document.getElementById("header");

  if (!target) {
    return 0;
  }

  const style = window.getComputedStyle(target);
  const stickyTop = parseFloat(style.top);

  if (style.position === "sticky" && !Number.isNaN(stickyTop)) {
    return stickyTop + target.offsetHeight;
  }

  return target.getBoundingClientRect().bottom;
};

interface ResourceNavProviderProps {
  navOptions: NavOption[];
  children: ReactNode;
  options?: ResourceNavOptions;
}

export const ResourceNavProvider = ({
  navOptions,
  children,
}: ResourceNavProviderProps) => {
  const [options, setOptions] = useState<ResourceNavOptions>({
    additionalOffset: 0,
  });
  const [currentSection, setCurrentSection] = useState<string>(
    navOptions[0].value,
  );
  const headerPositions = useRef<Record<string, number>>({});

  const [handleScroll] = useDebounce(() => {
    const offsetBottom =
      getOffsetBottom(options.offsetElement) + (options.additionalOffset ?? 0);
    let closestHeader = navOptions[0].value;

    for (const header in headerPositions.current) {
      if (headerPositions.current[header] > window.scrollY + offsetBottom) {
        break;
      }

      closestHeader = header;
    }

    setCurrentSection(closestHeader);
  }, 100);

  const onSelect = (sectionId: string) => {
    setCurrentSection(sectionId);

    // Selecting re-labels the picker, and a longer label wraps to another line.
    // Scroll on the next frame so the offset is measured against the height the
    // element settles at rather than the one it is leaving.
    requestAnimationFrame(() => scrollToSection(sectionId));
  };

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);

    if (section) {
      const top =
        section.getBoundingClientRect().top +
        window.scrollY -
        getOffsetBottom(options.offsetElement) -
        (options.additionalOffset ?? 0);

      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const getHeaderPositions = () => {
      navOptions.forEach((option) => {
        const section = document.getElementById(option.value);

        if (section) {
          headerPositions.current[option.id] =
            section.offsetTop - section.offsetHeight;
        }
      });
    };

    getHeaderPositions();
    window.addEventListener("resize", getHeaderPositions);

    return () => {
      window.removeEventListener("resize", getHeaderPositions);
    };
  }, [navOptions]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll, options.offsetElement, options.additionalOffset]);

  return (
    <ResourceNavContext.Provider
      value={{ currentSection, onSelect, options, setOptions }}
    >
      {children}
    </ResourceNavContext.Provider>
  );
};

export const useResourceNav = (): ResourceNavContextValue => {
  const ctx = useContext(ResourceNavContext);

  if (!ctx) {
    throw new Error("useResourceNav must be used within a ResourceNavProvider");
  }

  return ctx;
};
